import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const UpcomingMeetings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchMeetings();
    }
  }, [user?.id]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      
      // Fetch meetings where user is in attendee_ids array
      const { data, error } = await supabase
        ?.from('meetings')
        ?.select(`
          id,
          title,
          description,
          meeting_type,
          meeting_date,
          meeting_time,
          duration_minutes,
          location_type,
          location_details,
          google_meet_link,
          status,
          attendee_ids,
          team_id,
          teams(team_name)
        `)
        ?.contains('attendee_ids', [user?.id])
        ?.gte('meeting_date', new Date()?.toISOString()?.split('T')?.[0])
        ?.order('meeting_date', { ascending: true })
        ?.order('meeting_time', { ascending: true })
        ?.limit(5);

      if (error) throw error;

      // Format meetings
      const formattedMeetings = (data || [])?.map(m => ({
        id: m?.id,
        title: m?.title,
        teamName: m?.teams?.team_name || 'Organization-wide',
        date: m?.meeting_date,
        time: m?.meeting_time,
        duration: `${m?.duration_minutes} minutes`,
        type: m?.meeting_type || 'Meeting',
        location: m?.location_type === 'virtual' ? (m?.google_meet_link || 'Virtual') : (m?.location_details || 'In-person'),
        status: m?.status === 'scheduled' ? 'Confirmed' : m?.status === 'cancelled' ? 'Cancelled' : 'Pending',
        attendees: m?.attendee_ids?.length || 0
      }));

      setMeetings(formattedMeetings);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Confirmed': 'bg-success/10 text-success',
      'Pending': 'bg-warning/10 text-warning',
      'Cancelled': 'bg-error/10 text-error'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getMeetingTypeIcon = (type) => {
    const icons = {
      'Review Meeting': 'Eye',
      'Demo Session': 'Monitor',
      'Final Presentation': 'Presentation',
      'Kickoff Meeting': 'Play',
      'Check-in': 'MessageSquare'
    };
    return icons?.[type] || 'Calendar';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow?.setDate(tomorrow?.getDate() + 1);

    if (date?.toDateString() === today?.toDateString()) {
      return 'Today';
    } else if (date?.toDateString() === tomorrow?.toDateString()) {
      return 'Tomorrow';
    } else {
      return date?.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const isUpcoming = (dateString, timeString) => {
    const meetingDateTime = new Date(`${dateString} ${timeString}`);
    return meetingDateTime > new Date();
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft">
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold text-foreground">Upcoming Meetings</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="Plus"
            onClick={() => console.log('Schedule new meeting')}
          />
        </div>
      </div>
      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="space-y-3 sm:space-y-4">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="animate-pulse border border-border rounded-lg p-3 sm:p-4">
                <div className="h-5 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-muted rounded w-1/2 mb-3"></div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="h-3 bg-muted rounded"></div>
                  <div className="h-3 bg-muted rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : meetings?.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {meetings?.map((meeting) => (
              <div key={meeting?.id} className="border border-border rounded-lg p-3 sm:p-4 hover:bg-muted/30 transition-micro">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
                    <div className="p-2 bg-primary/10 text-primary rounded-lg flex-shrink-0">
                      <Icon name={getMeetingTypeIcon(meeting?.type)} size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground truncate">{meeting?.title}</h4>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">with {meeting?.teamName}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${getStatusColor(meeting?.status)}`}>
                    {meeting?.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={14} />
                    <span className="truncate">{formatDate(meeting?.date)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} />
                    <span className="truncate">{meeting?.time} ({meeting?.duration})</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={14} />
                    <span className="truncate">{meeting?.location}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Users" size={14} />
                    <span>{meeting?.attendees} attendees</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-xs text-muted-foreground">{meeting?.type}</span>
                  <div className="flex items-center space-x-2">
                    {isUpcoming(meeting?.date, meeting?.time) && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Video"
                          onClick={() => console.log('Join meeting')}
                          className="text-xs"
                        >
                          Join
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Calendar"
                          onClick={() => console.log('Add to calendar')}
                          className="text-xs hidden sm:flex"
                        >
                          Add to Calendar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8">
            <Icon name="Calendar" size={40} className="sm:w-12 sm:h-12 mx-auto text-muted-foreground mb-3 sm:mb-4" />
            <p className="text-xs sm:text-sm text-muted-foreground mb-4">No upcoming meetings scheduled.</p>
            <Button
              variant="outline"
              className="text-xs sm:text-sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => console.log('Schedule meeting')}
            >
              Schedule Meeting
            </Button>
          </div>
        )}

        {meetings?.length > 0 && (
          <div className="mt-4 sm:mt-6 pt-4 border-t border-border">
            {/*<Button
              variant="ghost"
              fullWidth
              iconName="Calendar"
              iconPosition="left"
              onClick={() => console.log('View calendar')}
              className="text-xs sm:text-sm"
            >
              View Full Calendar
            </Button>*/}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingMeetings;